import type {
  IAPMAppDataService,
  InFieldUser,
  TaskInterval,
  TaskIntervalUpsertType,
} from '@cognite/apm-client';
import { FDMClient } from '@cognite/fdm-client';
import type { CogniteClientDependencies } from '@cognite/fdm-client';

import type { IIntervalService } from './interval-service-interface';

export type APMClientDependencies = {
  intervals: Pick<
    IAPMAppDataService<TaskIntervalUpsertType>,
    'list' | 'upsert' | 'delete'
  >;
  userInstanceSpace: string;
};

export class IntervalService extends FDMClient implements IIntervalService {
  apmClient: APMClientDependencies;
  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies
  ) {
    super(client);
    this.apmClient = apmClient;
  }

  async createIntervals(intervals: TaskInterval[], user: InFieldUser) {
    const upsertedIntervals = intervals.map((interval) => ({
      ...interval,
      createdBy: user,
    }));
    await this.upsertIntervals(upsertedIntervals);
  }

  async updateIntervals(intervals: TaskInterval[], user: InFieldUser) {
    const upsertedIntervals = intervals.map((interval) => ({
      ...interval,
      updatedBy: user,
    }));
    await this.upsertIntervals(upsertedIntervals);
  }

  async upsertIntervals(intervals: TaskInterval[]) {
    const intervalsToUpsert = intervals.map((interval) => {
      return {
        ...interval,
        updatedBy: interval.updatedBy?.externalId
          ? {
              externalId: interval.updatedBy?.externalId,
              space: this.apmClient.userInstanceSpace,
            }
          : undefined,
        createdBy: interval.createdBy?.externalId
          ? {
              externalId: interval.createdBy?.externalId,
              space: this.apmClient.userInstanceSpace,
            }
          : undefined,
        template: undefined,
        templateItems: undefined,
      };
    });

    await this.apmClient.intervals.upsert(intervalsToUpsert);
  }

  async deleteIntervals(externalIds: string[]) {
    await this.apmClient.intervals.delete(externalIds);
  }
}

export const createIntervalService = (
  ...args: ConstructorParameters<typeof IntervalService>
): IIntervalService => new IntervalService(...args);
